import { IObserver } from '@1js/oil-externals/rx';
import { AuthType } from '../../shared/shared-context/client-startup-config.service';
import { SearchResultType } from '../search-instrumentation.service';
import { TellMeSearchSuggestionProvider } from './tellme.search-suggestion-provider';
import { TemplatePreviewItem } from './template.preview-suggestion-renderer';

/*
 * Data provider for template preview search result.
 */
export class TemplatePreviewSuggestionProvider extends TellMeSearchSuggestionProvider {

    private renderedResults: Array<string> = [];
    private readonly maxCount: number = 3;
    private readonly templateId: string = 'templatePreview';
    private readonly seeMoreInHeaderId: string = 'seeMoreInHeader';
    private readonly templateURL: string = `https://templates.office.com/${TemplatePreviewSuggestionProvider.contextService.sharedContext.officeMarket}/Search/results?query=`;
    private readonly templateUpsellURL = `https://www.microsoft.com/${TemplatePreviewSuggestionProvider.contextService.sharedContext.officeMarket}/microsoft-365/premium-templates-with-microsoft-365?ocid=`;
    private readonly templatePlatform: string = '&platform=Wac';

    public getId(): string {
        return 'templatePreview';
    }

    public getSearchSuggestion(searchText: string, searchSuggestion: Object, observer: IObserver<any>) {

        const jsonResponse = searchSuggestion;
        this.renderedResults = [];
        let templateSuggestions;

        try {
            if (jsonResponse['Groups'] && jsonResponse['Groups'].length > 0) {
                for (let groupCount = 0; groupCount < jsonResponse['Groups'].length; groupCount++ ) {
                    if (jsonResponse['Groups'][groupCount]['GroupType'] === 'Template') {
                        templateSuggestions = jsonResponse['Groups'][groupCount]['Suggestions'];
                        break;
                    }
                }

                if (templateSuggestions) {
                    let numOfTemplatesAcquired = 0;
                    for (let count = 0; numOfTemplatesAcquired < this.maxCount && count < templateSuggestions.length; count++ ) {
                        // Premium templates will be enabled once Dime is integrated.
                        if (!templateSuggestions[count]['IsPremium'] || TemplatePreviewSuggestionProvider.searchConfigService.config.premiumTemplatesForSearchEnabled || TemplatePreviewSuggestionProvider.clientStartupConfigService.config.authType === AuthType.OrgId) {
                            const isPwa = TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.isPwaInUwp;
                            const templateUpsellURL = isPwa ? this.templateUpsellURL + 'cmm6pexr7oa' : this.templateUpsellURL + 'cmm6es9k55z';
                            observer.onNext(
                                {
                                    contentLauncherEnabled: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.contentLauncherEnabled,
                                    createDocumentService: TemplatePreviewSuggestionProvider.createDocumentService,
                                    deviceDetector: TemplatePreviewSuggestionProvider.deviceDetector,
                                    id: this.templateId + numOfTemplatesAcquired.toString(),
                                    isEndOfResult: false,
                                    isMSA: TemplatePreviewSuggestionProvider.clientStartupConfigService.config.authType === AuthType.MSA,
                                    isPwa,
                                    isSubscribed: TemplatePreviewSuggestionProvider.isSubscribedUser,
                                    isDarkMode: TemplatePreviewSuggestionProvider.isDarkMode,
                                    searchConfigService: TemplatePreviewSuggestionProvider.searchConfigService,
                                    searchInstrumentationService: TemplatePreviewSuggestionProvider.searchInstrumentationService,
                                    searchText,
                                    templatePreviewData: this.getTemplatePreviewData(templateSuggestions, count, numOfTemplatesAcquired),
                                    templateUpsellURL,
                                    urlLauncherService: TemplatePreviewSuggestionProvider.urlLauncherService
                                });
                            numOfTemplatesAcquired++;
                        }

                        // Sending required props to render see more template button.
                        if ((numOfTemplatesAcquired === this.maxCount) || (count === templateSuggestions.length - 1)) {
                            observer.onNext(
                                {
                                    contentLauncherEnabled: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.contentLauncherEnabled,
                                    createDocumentService: TemplatePreviewSuggestionProvider.createDocumentService,
                                    deviceDetector: TemplatePreviewSuggestionProvider.deviceDetector,
                                    id: this.seeMoreInHeaderId,
                                    isEndOfResult: true,
                                    isMSA: TemplatePreviewSuggestionProvider.clientStartupConfigService.config.authType === AuthType.MSA,
                                    isPwa: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.isPwaInUwp,
                                    isSubscribed: TemplatePreviewSuggestionProvider.isSubscribedUser,
                                    isDarkMode: TemplatePreviewSuggestionProvider.isDarkMode,
                                    ngZone: TemplatePreviewSuggestionProvider.ngZone,
                                    officeRouteService: TemplatePreviewSuggestionProvider.officeRouteService,
                                    searchConfigService: TemplatePreviewSuggestionProvider.searchConfigService,
                                    searchInstrumentationService: TemplatePreviewSuggestionProvider.searchInstrumentationService,
                                    searchText,
                                    templatePreviewData: null,
                                    templateUpsellURL: this.templateUpsellURL,
                                    urlLauncherService: TemplatePreviewSuggestionProvider.urlLauncherService,
                                    seeMoreTemplateUrl: this.templateURL + searchText + this.templatePlatform
                                });

                            this.renderedResults.push(SearchResultType.Template);
                        }
                    }
                }
            }
        } catch (error) {
            observer.onCompleted();
        }

        TemplatePreviewSuggestionProvider.searchInstrumentationService.setRenderedProviderResults(SearchResultType.Template, this.renderedResults);
        observer.onCompleted();
    }

    public getMockedSearchSuggestion(searchText: string, observer: IObserver<any>) {
        for (let count = 0; count < 3; count++) {
            observer.onNext(
                {
                    contentLauncherEnabled: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.contentLauncherEnabled,
                    createDocumentService: TemplatePreviewSuggestionProvider.createDocumentService,
                    deviceDetector: TemplatePreviewSuggestionProvider.deviceDetector,
                    id: this.templateId + count.toString(),
                    isEndOfResult: false,
                    isPwa: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.isPwaInUwp,
                    isSubscribed: TemplatePreviewSuggestionProvider.isSubscribedUser,
                    isDarkMode: TemplatePreviewSuggestionProvider.isDarkMode,
                    searchConfigService: TemplatePreviewSuggestionProvider.searchConfigService,
                    searchInstrumentationService: TemplatePreviewSuggestionProvider.searchInstrumentationService,
                    searchText,
                    templatePreviewData: this.getMockedTemplatePreviewData(count + 1),
                    templateUpsellURL: this.templateUpsellURL,
                    urlLauncherService: TemplatePreviewSuggestionProvider.urlLauncherService
                });
        }

        observer.onNext(
            {
                contentLauncherEnabled: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.contentLauncherEnabled,
                createDocumentService: TemplatePreviewSuggestionProvider.createDocumentService,
                deviceDetector: TemplatePreviewSuggestionProvider.deviceDetector,
                id: this.seeMoreInHeaderId,
                isEndOfResult: true,
                isPwa: TemplatePreviewSuggestionProvider.capabilitiesService.capabilities.isPwaInUwp,
                isSubscribed: TemplatePreviewSuggestionProvider.isSubscribedUser,
                isDarkMode: TemplatePreviewSuggestionProvider.isDarkMode,
                ngZone: TemplatePreviewSuggestionProvider.ngZone,
                officeRouteService: TemplatePreviewSuggestionProvider.officeRouteService,
                searchConfigService: TemplatePreviewSuggestionProvider.searchConfigService,
                searchInstrumentationService: TemplatePreviewSuggestionProvider.searchInstrumentationService,
                searchText,
                templatePreviewData: null,
                templateUpsellURL: this.templateUpsellURL,
                urlLauncherService: TemplatePreviewSuggestionProvider.urlLauncherService,
                seeMoreTemplateUrl: this.templateURL + searchText + this.templatePlatform
            });

        observer.onCompleted();
    }

    private getTemplatePreviewData(templateRawData: Array<object>, count: number, position: number): TemplatePreviewItem {

        let templateData: TemplatePreviewItem;

        try {
            if (count < templateRawData.length) {
                templateData = {
                        AssetId: templateRawData[count]['AssetId'],
                        Description: templateRawData[count]['Description'],
                        TemplateBinaryUrl: templateRawData[count]['TemplateBinaryUrl'],
                        Market: TemplatePreviewSuggestionProvider.contextService.sharedContext.officeMarket,
                        NumberOfRatings: templateRawData[count]['RecommendationModifier'],
                        IsPremium:  TemplatePreviewSuggestionProvider.clientStartupConfigService.config.authType === AuthType.OrgId ? false : templateRawData[count]['IsPremium'],
                        RecsMod: null,
                        Title: templateRawData[count]['Title'],
                        Thumbnails: null,
                        IsBlank: false,
                        Categories: null,
                        application: templateRawData[count]['Application'],
                        imageHeight: templateRawData[count]['Images'][0]['Height'], // Images are 418px * 321px or 321px * 418px.
                        imageWidth: templateRawData[count]['Images'][0]['Width'],
                        imageUrl: templateRawData[count]['Images'][0]['Url'],
                        position: (position + 1).toString()
                    };
                this.renderedResults.push(templateRawData[count]['AssetId'] + ':' + templateRawData[count]['Application']);
            }
        } catch (error) {
            return templateData;
        }

        return templateData;
    }

    // Mocked template data for visual testing only.
    private getMockedTemplatePreviewData(position: number): TemplatePreviewItem {
        return {
            AssetId: 'TM67008005',
            Categories: null,
            Description: 'This column resume template is useful both as a sample resume and to show off your accomplishments.',
            IsBlank: false,
            IsPremium: false,
            Market: 'en-US',
            NumberOfRatings: undefined,
            RecsMod: null,
            TemplateBinaryUrl: 'https://binaries.templates.cdn.office.net/support/templates/en-us/tf67008005_wac.docx',
            Thumbnails: null,
            Title: 'Columns resume',
            application: 'Word',
            imageHeight: 600,
            imageUrl: 'https://binaries.templates.cdn.office.net/support/templates/en-us/lt67008005.png',
            imageWidth: 462,
            position: position.toString()
        };
    }
}
